import { parseRoomPath } from "@/deal-room/room-path";
import posthog from "posthog-js";

/**
 * PostHog analytics for the Supplier Deal Room.
 *
 * Captures: pageviews, autocapture, heatmaps, AND session replay. Replay is ON
 * so we can watch how each supplier walks their room. All input fields are
 * masked (maskAllInputs) so typed PII / form values / uploads are never
 * recorded; the room's own copy (Bison content shown to the supplier) is.
 *
 * The project API key (phc_...) is a PUBLIC client key by design, so it is
 * safe to ship in the bundle. Override per-environment with VITE_POSTHOG_KEY.
 *
 * Heatmaps are URL-scoped in PostHog. Every supplier room is its own slug
 * (/deal-room/<slug>/<page>), so to see an AGGREGATE heatmap across suppliers for a
 * given page, use a wildcard URL match in the Heatmaps tool: replace the slug
 * segment with a star wildcard. Every event is also tagged with deal_room_slug
 * and deal_room_page (see before_send) for filtering in insights.
 *
 * NOTE ON VERIFICATION: PostHog silently drops events from clients its
 * user-agent filter flags as bots — including headless Chromium (Playwright,
 * `navigator.webdriver`). So an automated/headless smoke test will see zero
 * events even though the integration is fine; verify with a real browser, or
 * temporarily `posthog.set_config({ opt_out_useragent_filter: true })`.
 */

const POSTHOG_KEY =
    import.meta.env.VITE_POSTHOG_KEY ??
    "phc_rmMQqnTf5fGYtzxP3SKmBcVufHau7u4BCxi6JwxdQN8b";

// Route ingestion + assets through the PostHog managed reverse proxy on our own
// domain (t.bisoncommerce.com). This keeps tracking requests first-party, so
// ad blockers that block us.i.posthog.com don't silently drop 10-25% of events.
const POSTHOG_HOST =
    import.meta.env.VITE_POSTHOG_HOST ?? "https://t.bisoncommerce.com";

let started = false;

/**
 * Initialise PostHog. Idempotent and safe to call before render. Skips
 * localhost/dev unless a key is explicitly configured, so the production
 * project isn't polluted with dev traffic.
 */
export function initPostHog(): void {
    if (started) return;
    if (typeof window === "undefined") return;

    // Opt localhost in by setting VITE_POSTHOG_KEY in a local .env.
    if (import.meta.env.DEV && !import.meta.env.VITE_POSTHOG_KEY) return;

    posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        ui_host: "https://us.posthog.com",
        debug: import.meta.env.DEV,
        // The reason we're here: collect click / scroll / rageclick heatmap data.
        enable_heatmaps: true,
        autocapture: true,
        // Session replay ON by default. Mask every input so typed PII / form
        // values / upload filenames are never captured — the room's own copy
        // (our content shown to the supplier) is still recorded.
        disable_session_recording: false,
        session_recording: {
            maskAllInputs: true,
        },
        // Let PostHog capture pageviews itself, SPA-aware: it fires on History
        // API navigations (React Router), including the initial load. This is
        // what kicks off the capture pipeline.
        capture_pageview: "history_change",
        capture_pageleave: true,
        // Anonymous supplier visitors don't need a person profile for heatmaps.
        person_profiles: "identified_only",
        // Tag every event (pageview, autocapture, heatmap) with the deal-room
        // slug + page, so heatmaps and insights can be filtered per room / page.
        before_send: (event) => {
            if (event) {
                const parsed = parseRoomPath(window.location.pathname);
                event.properties = {
                    ...event.properties,
                    deal_room_slug: parsed?.slug,
                    deal_room_page: parsed ? (parsed.page ?? "(home)") : undefined,
                    room_type: parsed?.type,
                };
            }
            return event;
        },
    });

    started = true;
}

/**
 * Attach the signed-in viewer to their PostHog person so replays, heatmaps
 * and event streams can be read per person and per room, not just per
 * anonymous device. Called once the session is known (useCurrentRoomUser).
 * Email is the distinct id; the room slug is a group so "everything this
 * supplier's people did" is one filter. Safe to call repeatedly.
 */
export function identifyViewer(user: {
    email: string;
    name?: string;
    slug?: string;
    staff?: boolean;
}): void {
    if (!started) return;
    posthog.identify(user.email, {
        email: user.email,
        name: user.name,
        bison_staff: Boolean(user.staff),
    });
    if (user.slug) posthog.group("deal_room", user.slug);
}

export { posthog };
